"use client";

import { businessFiltersAtom } from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { useNearbyListings } from "@/app/(saas)/(app)/businesses/_hooks/use-nearby-listings";
import { ListingPreview } from "@/components/listings/listing-preview";
import { valuesWithAppName } from "@/lib/i18n";
import { buildListingUrl } from "@/lib/url";
import { useAtomValue } from "jotai";
import { ArrowRightIcon, MapPinIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useMemo } from "react";
import slugify from "slugify";

export type BusinessListNearbySectionProps = {
  excludeIds: number[];
};

export function BusinessListNearbySection({
  excludeIds,
}: BusinessListNearbySectionProps) {
  const t = useTranslations();
  const filters = useAtomValue(businessFiltersAtom);

  const { listings, isNearby, isLoading } = useNearbyListings({
    enabled: true,
    excludeIds,
  });

  const regionLinks = useMemo(() => {
    const links: Array<{ label: string; href: string }> = [];
    const location = filters.location;
    if (!location) return links;

    const { country, region } = location.context;
    const countryCode = country?.code?.toLowerCase();

    if (region && countryCode) {
      links.push({
        label: t("saas.search.nearbySection.exploreRegion", {
          region: region.name,
        }),
        href: `/businesses/${countryCode}/${slugify(region.name, { lower: true })}`,
      });
    }

    if (country && countryCode) {
      links.push({
        label: t("saas.search.nearbySection.exploreCountry", {
          country: country.name,
        }),
        href: `/businesses/${countryCode}`,
      });
    }

    return links;
  }, [filters.location, t]);

  if (!isLoading && listings.length === 0) {
    return null;
  }

  return (
    <div className="mt-4 rounded-xl border border-border bg-secondary p-5">
      {isLoading && (
        <div className="space-y-4">
          <div className="h-6 w-56 animate-pulse rounded bg-muted" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div
                key={`nearby-skel-${i}`}
                className="h-[280px] animate-pulse rounded-xl border border-border bg-card"
              />
            ))}
          </div>
        </div>
      )}

      {!isLoading && listings.length > 0 && (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-card shadow-sm">
                <MapPinIcon className="h-4 w-4 text-muted-foreground" />
              </div>
              <h3 className="text-base font-semibold tracking-[-0.01em] text-foreground">
                {isNearby
                  ? t("saas.search.nearbySection.nearbyTitle")
                  : t(
                      "saas.search.nearbySection.featuredTitle",
                      valuesWithAppName(t, {}),
                    )}
              </h3>
            </div>

            {regionLinks.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {regionLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-1.5 text-[13px] font-medium text-foreground transition-all duration-200 hover:-translate-y-px hover:border-border hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]"
                  >
                    <MapPinIcon className="h-3.5 w-3.5 text-muted-foreground" />
                    {link.label}
                    <ArrowRightIcon className="h-3 w-3 text-muted-foreground" />
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {listings.map((listing) => (
              <a
                key={listing.slug ?? `nearby-${listing.id}`}
                href={buildListingUrl(listing)}
                className="flex flex-col rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground"
                target="_blank"
                rel="noreferrer"
              >
                <ListingPreview listing={listing} />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

BusinessListNearbySection.displayName = "BusinessListNearbySection";
